import { useEffect } from "react";

type ShortcutKey =
  | "ctrl+k"
  | "ctrl+z"
  | "ctrl+shift+z"
  | "n"
  | "e"
  | "1"
  | "2"
  | "3"
  | "4";

type ShortcutMap = Partial<Record<ShortcutKey, () => void>>;

function isInputElement(
  element: unknown,
): element is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement {
  if (!(element instanceof Element)) return false;
  const tag = element.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select";
}

function shouldIgnoreShortcut(target: EventTarget | null): boolean {
  return isInputElement(target);
}

function normalizeKey(event: KeyboardEvent): ShortcutKey | null {
  const key = event.key.toLowerCase();
  const ctrl = event.ctrlKey || event.metaKey;
  const shift = event.shiftKey;

  if (ctrl && !shift && key === "k") return "ctrl+k";
  if (ctrl && !shift && key === "z") return "ctrl+z";
  if (ctrl && shift && key === "z") return "ctrl+shift+z";
  if (!ctrl && !shift && key === "n") return "n";
  if (!ctrl && !shift && key === "e") return "e";
  if (!ctrl && !shift && key === "1") return "1";
  if (!ctrl && !shift && key === "2") return "2";
  if (!ctrl && !shift && key === "3") return "3";
  if (!ctrl && !shift && key === "4") return "4";

  return null;
}

export function useKeyboard(shortcuts: ShortcutMap): void {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (shouldIgnoreShortcut(event.target)) return;

      const shortcut = normalizeKey(event);
      if (!shortcut) return;

      const handler = shortcuts[shortcut];
      if (!handler) return;

      event.preventDefault();
      handler();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [shortcuts]);
}
